/* eslint-disable react-hooks/exhaustive-deps */
import * as THREE from 'three';
import React, { useMemo } from 'react';
import { useThree } from '@react-three/fiber';

import { const_var, params } from '../../../../../../../redux/reducers/reducer';

import { delModel, extrudeSettings, roofPattern, textureAnisotropy, wainscotTrimModel } from '../../../../../../../utils/Functions';
import { CSG } from '../../../../../../../assets/libs/csg-lib';
import { MaterialOuterWallNew, MaterialWainScotTrim, MaterialWainScotTrimNew, MaterialWainScotWallNew } from '../../../../../CustomMaterial';

const OutsideWallModel = () => {
    const { gl } = useThree();
    
    const canvasElement = document.getElementsByClassName("buildingcanvas")
    const isPattern = (canvasElement != undefined && canvasElement[0]?.clientWidth > 1050 && !params.p_v_w) ? true : false

     

    const wallModel = useMemo(() => {
        const leftHeight = (params.singleSlope && params.freeStandingSide === "left") ? params.p_h - ((params.p_r_p / 12) * params.p_w) : params.p_h
        const rightHeight = (params.singleSlope && params.freeStandingSide === "right") ? params.p_h + ((params.p_r_p / 12) * params.p_w) : params.p_h
        
        let model = new THREE.Shape();

        if (params.p_r_s === 1) {
            const innerRoofWidth = params.p_w - 0.01
            const innerRoofPorchSize = params.p_r_p - 0.02
            
            model.moveTo(-innerRoofWidth / 2 + 0.0001, params.p_h);

            model.lineTo(-innerRoofWidth / 2, params.p_h + 0.3);
            model.lineTo(-innerRoofWidth / 2 + 0.02, params.p_h + 0.34);
            model.lineTo(-innerRoofWidth / 2 + 0.055, params.p_h + 0.38);
            model.lineTo(-innerRoofWidth / 2 + 0.1, params.p_h + 0.4);
    
            model.lineTo(0, params.p_h + (innerRoofPorchSize / 12) * innerRoofWidth / 2 + 0.4);
    
            model.lineTo(innerRoofWidth / 2 - 0.1, params.p_h + 0.4);
            model.lineTo(innerRoofWidth / 2 - 0.055, params.p_h + 0.38);
            model.lineTo(innerRoofWidth / 2 - 0.02, params.p_h + 0.34);
            model.lineTo(innerRoofWidth / 2, params.p_h + 0.3);
    
            model.lineTo(innerRoofWidth / 2 +0.005, params.p_h);

            model.lineTo(innerRoofWidth / 2 +0.005, 0.01);
            model.lineTo(-innerRoofWidth / 2 +0.005, 0.01);
    
            model.closePath()
    
        } else if (params.p_r_s === 2 || params.p_r_s === 3) {
            model.moveTo(-params.p_w / 2, leftHeight - 0.001);
            if(!params.singleSlope) model.lineTo(0, params.p_h + (params.p_r_p / 12) * params.p_w / 2);
            model.lineTo(params.p_w / 2, rightHeight - 0.01);
            model.lineTo(params.p_w / 2, 0.01);
            model.lineTo(-params.p_w / 2, 0.01);
            model.closePath()
        }

        return model;
    
    }, [params.p_w, params.p_h, params.p_r_s, params.p_r_p, params.singleSlope, params.freeStandingSide, params.p_w_c_n])

    /************** cutting hole using CSG ***************/

    const mainWallModel = useMemo(() => {
        const regularHeight = params.p_r_s == 1 ? 0.4 : 0

        let verticalHorizontalPos = {"x": params.p_w / 2 ,"y" : (params.p_w_c_n ? 2.935 : 0), "z" : params.p_d / -2 - 0.16}
        let verticalHorizontalRot = {"x":0,"y" : Math.PI / - 2,"z" : Math.PI / 2}
        let patternLength = params.p_h + ((params.p_r_p / 12) * (params.p_w/2)) + regularHeight + (params.p_w_c_n ? -2.935 : 0)
        let patternDepth = params.p_w

        const meshWallModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength, isPattern), extrudeSettings(patternDepth)))
        
        const meshDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('backStorageBack', 'wall'), extrudeSettings(1)))
        const meshRightOffsetWithDoorDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('right', 'wall'), extrudeSettings(1)))
        const meshLeftOffsetWithDoorDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(delModel('left', 'wall'), extrudeSettings(1)))

        let meshWallModel_Temp = meshWallModel.clone();
        meshWallModel_Temp.position.set(verticalHorizontalPos.x,verticalHorizontalPos.y,verticalHorizontalPos.z);
        meshWallModel_Temp.rotation.set(verticalHorizontalRot.x,verticalHorizontalRot.y,verticalHorizontalRot.z);
        meshWallModel_Temp.updateMatrixWorld();

        const meshGableDelModel = new THREE.Mesh(new THREE.ExtrudeGeometry(wallModel, extrudeSettings(1)))
        let meshGableDelModel_Temp = meshGableDelModel.clone();
        meshGableDelModel_Temp.position.set(0,0,params.p_d / -2 - 0.5);
        meshGableDelModel_Temp.updateMatrixWorld();

        let meshDelModel_Temp = meshDelModel.clone();
        meshDelModel_Temp.position.set(0, 0, params.p_d / -2 - 0.5);
        meshDelModel_Temp.updateMatrixWorld();

        // mesh to cut the dutch model
        let meshRightOffsetWithDoorDelModel_Temp = meshRightOffsetWithDoorDelModel.clone();
        meshRightOffsetWithDoorDelModel_Temp.position.set(params.p_w / 2 + 0.75, 0, -params.endStoragePositionZ);
        meshRightOffsetWithDoorDelModel_Temp.rotateY(-Math.PI / 2)
        meshRightOffsetWithDoorDelModel_Temp.updateMatrixWorld();

        let meshLeftOffsetWithDoorDelModel_Temp = meshLeftOffsetWithDoorDelModel.clone();
        meshLeftOffsetWithDoorDelModel_Temp.position.set(-params.p_w / 2 + 0.25, 0, -params.endStoragePositionZ);
        meshLeftOffsetWithDoorDelModel_Temp.rotateY(-Math.PI / 2)
        meshLeftOffsetWithDoorDelModel_Temp.updateMatrixWorld();

        const meshWallModel_CSG = CSG.fromMesh(meshWallModel_Temp);
        const meshDelModel_CSG = CSG.fromMesh(meshDelModel_Temp);
        const meshGableDelModel_CSG = CSG.fromMesh(meshGableDelModel_Temp);
        const meshRightOffsetWithDoorDelModel_CSG = CSG.fromMesh(meshRightOffsetWithDoorDelModel_Temp);
        const meshLeftOffsetWithDoorDelModel_CSG = CSG.fromMesh(meshLeftOffsetWithDoorDelModel_Temp);

        const wallModel_CSG = meshWallModel_CSG.subtract(meshDelModel_CSG);
        const gableModel_CSG = wallModel_CSG.intersect(meshGableDelModel_CSG);
        const rightWallDel_CSG = gableModel_CSG.subtract(meshRightOffsetWithDoorDelModel_CSG);
        const leftWallDel_CSG = rightWallDel_CSG.subtract(meshLeftOffsetWithDoorDelModel_CSG);

        const CSGWallModel = CSG.toMesh(leftWallDel_CSG,new THREE.Matrix4())

        /************** cutting hole in wainscot using CSG ***************/
        let wainscotVerticalHorizontalPos = {"x": params.p_w / 2 ,"y" : 0, "z" : params.p_d / -2 - 0.16}
        let wainscotVerticalHorizontalRot = {"x":0,"y" : Math.PI / - 2,"z" : Math.PI / 2}
        let lengthPattern = 3
        let depthPattern = patternDepth
        const meshWainscotModel = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(lengthPattern, isPattern), extrudeSettings(depthPattern)))
        let meshWainscotModel_Temp = meshWainscotModel.clone();
        meshWainscotModel_Temp.position.set(wainscotVerticalHorizontalPos.x,wainscotVerticalHorizontalPos.y,wainscotVerticalHorizontalPos.z);
        meshWainscotModel_Temp.rotation.set(wainscotVerticalHorizontalRot.x,wainscotVerticalHorizontalRot.y,wainscotVerticalHorizontalRot.z);
        meshWainscotModel_Temp.updateMatrixWorld();

        const meshWainscotModel_CSG = CSG.fromMesh(meshWainscotModel_Temp);

        const wainscotModel_CSG = meshWainscotModel_CSG.subtract(meshDelModel_CSG);
        const rightWainscotWallDel_CSG = wainscotModel_CSG.subtract(meshRightOffsetWithDoorDelModel_CSG);
        const leftWainscotWallDel_CSG = rightWainscotWallDel_CSG.subtract(meshLeftOffsetWithDoorDelModel_CSG);
        const CSGWainscotModel = CSG.toMesh(
            leftWainscotWallDel_CSG,
            new THREE.Matrix4()
        )

        /************** cutting hole in Wainscot Trim using CSG ***************/

        const meshWainscotTrimModel = new THREE.Mesh(new THREE.ExtrudeGeometry(wainscotTrimModel(0.14, 0.17), extrudeSettings(params.p_w)))
        let meshWainscotTrimModel_Temp = meshWainscotTrimModel.clone();
        meshWainscotTrimModel_Temp.position.x = -params.p_w / 2;
        meshWainscotTrimModel_Temp.position.y = 3
        meshWainscotTrimModel_Temp.position.z = -params.p_d / 2 - 0.155
        meshWainscotTrimModel_Temp.rotation.y = Math.PI/2
        meshWainscotTrimModel_Temp.updateMatrixWorld();

        const meshWainscotTrimModel_CSG = CSG.fromMesh(meshWainscotTrimModel_Temp);

        const wainscotTrimModel_CSG = meshWainscotTrimModel_CSG.subtract(meshDelModel_CSG);
        const leftTrimDelModel_CSG = wainscotTrimModel_CSG.subtract(meshLeftOffsetWithDoorDelModel_CSG);
        const rightTrimDelModel_CSG = leftTrimDelModel_CSG.subtract(meshRightOffsetWithDoorDelModel_CSG);

        const CSGWainscotTrimModel = CSG.toMesh(
            rightTrimDelModel_CSG,
            new THREE.Matrix4()
        )

        return {
            wall: CSGWallModel.geometry,
            wainScot: CSGWainscotModel.geometry,
            wTrim: CSGWainscotTrimModel.geometry
        }
    
        /****************************************** *********/
    }, [params.wallTexture, params.endStoragePositionZ, params.p_h, params.p_d,params.p_w, params.p_v_w, params.cB_addStorage_check_left,params.cB_addStorage_check_right, params.cB_addStorage_left,params.cB_addStorage_right, params.p_b_w, params.p_r_p, params.p_r_s, const_var.newDoorsArray, params.p_w_c_n]);

    return (
        <>
            {mainWallModel.wall &&
                <>
                    <mesh name="c_b_b_s_w" geometry={mainWallModel.wall} position={[0, 0, -0.05]} renderOrder={1}>
                        <MaterialOuterWallNew isPattern={isPattern} visibleSection={"center"} />
                    </mesh>
                </>
            }
            {params.p_w_c_n === true && mainWallModel.wainScot &&
                <mesh name="c_b_b_s_w_wainscot" geometry={mainWallModel.wainScot} position={[0, 0, -0.055]} renderOrder={1}>
                    <MaterialWainScotWallNew isPattern={isPattern} visibleSection={"center"} />
                </mesh>
            }

            {params.p_w_c_n === true && mainWallModel.wTrim &&
                <mesh name="c_b_b_s_w_wainscotRightTrim" geometry={mainWallModel.wTrim} position={[0, 0, -0.0525]}>
                    <MaterialWainScotTrimNew visibleSection={"center"}  />
                </mesh>
            }
        </>
    )
}

export default OutsideWallModel